<template>
  <div class="pa-4">
    <v-row justify="space-between">
      <v-col v-if="!noHeader">
        <div class="font-weight-bold basic--text text-center">
          {{ 'Programme Document Uploads'.toUpperCase() }}
        </div>
      </v-col>
    </v-row>

    <v-alert v-if="programmeSelectionPending" type="warning" class="mt-4">
      Select your final programme before uploading programme-specific documents.
    </v-alert>

    <v-alert v-else type="warning" class="mt-4">
      Upload the additional documents required for your programme. Only PDF, JPG, JPEG and PNG files up to 500 KB are accepted.
    </v-alert>

    <v-row v-if="!programmeSelectionPending && loading" justify="center" class="my-8">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </v-row>

    <template v-else-if="!programmeSelectionPending && requirements.length > 0">
      <v-card
        v-for="requirement in sortedRequirements"
        :key="requirement.id"
        outlined
        class="mb-4"
      >
        <v-card-title class="text-subtitle-1">
          {{ requirement.name }}
          <v-chip
            small
            class="ml-3"
            :color="requirement.mandatory ? 'error' : 'grey'"
            outlined
          >
            {{ requirement.mandatory ? 'Mandatory' : 'Optional' }}
          </v-chip>
          <v-chip small class="ml-2" color="info" outlined>
            {{ requirementModeLabel(requirement) }}
          </v-chip>
        </v-card-title>
        <v-card-text>
          <p v-if="requirement.description" class="grey--text text--darken-2">
            {{ requirement.description }}
          </p>

          <v-alert
            v-if="submissionFor(requirement)"
            type="success"
            class="mb-3"
          >
            <v-row align="center" no-gutters>
              <v-col>
                <strong>Uploaded:</strong> {{ submissionFor(requirement).fileName }}<br>
                <span class="text-caption">
                  {{ formatDate(submissionFor(requirement).uploadedAt) }}
                </span>
              </v-col>
              <v-col cols="auto">
                <v-btn small outlined color="primary" class="mr-2" @click="downloadSubmission(submissionFor(requirement).id)">
                  <v-icon small left>mdi-download</v-icon>
                  Download
                </v-btn>
                <v-btn small outlined color="warning" @click="startReplace(requirement)">
                  <v-icon small left>mdi-upload</v-icon>
                  Replace
                </v-btn>
                <v-btn small outlined color="error" class="ml-2" @click="confirmDelete(submissionFor(requirement))">
                  <v-icon small left>mdi-delete</v-icon>
                  Delete
                </v-btn>
              </v-col>
            </v-row>
          </v-alert>

          <div v-if="replacingRequirementId === requirement.id || !submissionFor(requirement)">
            <v-file-input
              v-model="selectedFiles[requirement.id]"
              :label="submissionFor(requirement) ? 'Select replacement file' : 'Select file'"
              outlined
              dense
              accept=".pdf,.jpg,.jpeg,.png"
              :rules="fileRules"
              prepend-icon="mdi-file-document-outline"
              show-size
            ></v-file-input>
            <v-card-actions class="pa-0">
              <v-spacer></v-spacer>
              <v-btn
                v-if="replacingRequirementId === requirement.id"
                text
                color="basic"
                @click="cancelReplace"
              >
                Cancel
              </v-btn>
              <v-btn
                color="primary"
                depressed
                :disabled="!canUpload(requirement)"
                :loading="uploadingFor === requirement.id"
                @click="uploadFile(requirement)"
              >
                {{ submissionFor(requirement) ? 'Replace' : 'Upload' }}
              </v-btn>
            </v-card-actions>
          </div>
        </v-card-text>
      </v-card>
    </template>

    <v-alert v-else-if="!programmeSelectionPending" type="info" class="mt-4">
      No additional documents are required for your programme.
    </v-alert>

    <v-card-actions class="mt-6">
      <v-spacer></v-spacer>
      <v-btn color="basic" class="white--text" depressed @click="$emit('back')">
        {{ noHeader ? 'Back' : 'Previous' }}
      </v-btn>
      <v-btn
        color="primary"
        depressed
        @click="success"
        :disabled="!canProceed"
      >
        {{ noHeader ? 'Save' : 'Proceed' }}
      </v-btn>
    </v-card-actions>

    <!-- Delete confirmation -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline">Delete Submission?</v-card-title>
        <v-card-text>
          Are you sure you want to delete <strong>{{ deleteItem.requirementName }}</strong>?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" :loading="deleting" @click="deleteSubmission">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <loading-dialog :show="loading"></loading-dialog>
    <message-box
      :show="messageShow"
      :message="message"
      :type="messageType"
      v-on:close-message="messageShow = false"
    ></message-box>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Api from "@/main/api";
import ErrorResolver from "@/shared/ErrorResolver";
import LoadingDialog from "@ui/components/LoadingDialog";
import MessageBox from "@ui/components/MessageBox";
import {
  APPLICATION_DOCUMENT_SIZE_ERROR,
  applicationDocumentSizeRule,
  isApplicationDocumentSizeAllowed,
} from "@/application/applicationDocumentValidation";

export default {
  name: "ProgrammeDocumentUploads",
  props: {
    noHeader: Boolean,
    autoAdvance: {
      type: Boolean,
      default: true,
    },
  },
  components: { LoadingDialog, MessageBox },

  data: () => ({
    loading: false,
    requirements: [],
    submissions: [],
    selectedFiles: {},
    uploadingFor: null,
    replacingRequirementId: null,
    deleteDialog: false,
    deleting: false,
    deleteItem: {},
    messageShow: false,
    message: "",
    messageType: "",
    fileRules: [
      (v) => !!v || "Please select a file to upload",
      applicationDocumentSizeRule,
      (v) =>
        !v ||
        [
          "application/pdf",
          "image/jpeg",
          "image/jpg",
          "image/png",
        ].includes(v.type) ||
        "Invalid file type. Only PDF, JPG, JPEG, and PNG files are allowed",
    ],
  }),

  computed: {
    ...mapGetters(["getUser"]),

    sortedRequirements() {
      return [...this.requirements].sort((a, b) => {
        const orderA = a.displayOrder || 0;
        const orderB = b.displayOrder || 0;
        return orderA - orderB || a.name.localeCompare(b.name);
      });
    },

    canProceed() {
      if (this.programmeSelectionPending) return false;
      if (this.requirements.length === 0) return true;
      return this.requirements
        .filter((r) => r.mandatory)
        .every((r) => this.submissionFor(r) != null);
    },

    programmeSelectionPending() {
      return this.getUser?.programme?.enableOptionSelection === true;
    },
  },

  watch: {
    getUser: function (val) {
      if (val && val.programme) {
        this.loadData();
      }
    },
  },

  methods: {
    success() {
      this.$emit("success");
    },

    back() {
      this.$emit("back");
    },

    async loadData() {
      if (this.programmeSelectionPending) return;

      if (!this.getUser || !this.getUser.programme) {
        this.success();
        return;
      }
      this.loading = true;
      try {
        const res = await Api.programmeDocuments.listRequirementsForProgramme(
          this.getUser.programme.id
        );
        this.requirements = (res.data || []).filter((requirement) =>
          this.isApplicableRequirement(requirement)
        );
        if (this.requirements.length === 0) {
          this.success();
          return;
        }
        await this.loadSubmissions();
        if (this.canProceed && this.autoAdvance) {
          this.success();
        }
      } catch (err) {
        ErrorResolver.checkErrors(this, err);
      } finally {
        this.loading = false;
      }
    },

    isApplicableRequirement(requirement) {
      if (requirement.appliesToAllModes !== false) return true;

      const applicantModeOfEntryId = this.getUser.modeOfEntry
        ? this.getUser.modeOfEntry.id
        : this.getUser.modeOfEntryId;

      return applicantModeOfEntryId != null &&
        String(requirement.modeOfEntryId) === String(applicantModeOfEntryId);
    },

    requirementModeLabel(requirement) {
      if (requirement.appliesToAllModes !== false) return "All modes of entry";
      if (requirement.modeOfEntryTitle) return requirement.modeOfEntryTitle;
      if (this.getUser.modeOfEntry && this.getUser.modeOfEntry.title) {
        return this.getUser.modeOfEntry.title;
      }
      return "Your mode of entry";
    },

    async loadSubmissions() {
      try {
        const res = await Api.programmeDocuments.listMySubmissions();
        this.submissions = res.data;
      } catch (err) {
        ErrorResolver.checkErrors(this, err);
      }
    },

    submissionFor(requirement) {
      return this.submissions.find(
        (s) => s.requirementId === requirement.id
      );
    },

    canUpload(requirement) {
      const file = this.selectedFiles[requirement.id];
      return (
        !this.programmeSelectionPending &&
        file &&
        isApplicationDocumentSizeAllowed(file) &&
        [
          "application/pdf",
          "image/jpeg",
          "image/jpg",
          "image/png",
        ].includes(file.type)
      );
    },

    async uploadFile(requirement) {
      const file = this.selectedFiles[requirement.id];
      if (!file) return;
      if (!this.canUpload(requirement)) {
        const message = isApplicationDocumentSizeAllowed(file)
          ? "Invalid file type. Only PDF, JPG, JPEG, and PNG files are allowed"
          : APPLICATION_DOCUMENT_SIZE_ERROR;
        this.showMessage(message, "error");
        return;
      }

      this.uploadingFor = requirement.id;
      const formData = new FormData();
      formData.append("requirementId", requirement.id);
      formData.append("file", file);

      try {
        await Api.programmeDocuments.uploadSubmission(formData);
        this.showMessage(
          `${requirement.name} uploaded successfully`,
          "success"
        );
        this.$set(this.selectedFiles, requirement.id, null);
        this.replacingRequirementId = null;
        await this.loadSubmissions();
        if (this.canProceed && this.autoAdvance) {
          this.success();
        }
      } catch (err) {
        ErrorResolver.checkErrors(this, err);
      } finally {
        this.uploadingFor = null;
      }
    },

    startReplace(requirement) {
      this.replacingRequirementId = requirement.id;
      this.$set(this.selectedFiles, requirement.id, null);
    },

    cancelReplace() {
      this.replacingRequirementId = null;
    },

    confirmDelete(submission) {
      this.deleteItem = submission;
      this.deleteDialog = true;
    },

    async deleteSubmission() {
      this.deleting = true;
      try {
        await Api.programmeDocuments.deleteSubmission(this.deleteItem.id);
        this.showMessage("Submission deleted successfully", "success");
        this.deleteDialog = false;
        await this.loadSubmissions();
      } catch (err) {
        ErrorResolver.checkErrors(this, err);
      } finally {
        this.deleting = false;
      }
    },

    async downloadSubmission(id) {
      this.loading = true;
      try {
        const res = await Api.programmeDocuments.downloadSubmission(id);
        this.saveBlob(res.data, res.headers["content-disposition"]);
      } catch (err) {
        ErrorResolver.checkErrors(this, err);
      } finally {
        this.loading = false;
      }
    },

    saveBlob(blob, contentDisposition) {
      let fileName = "document";
      if (contentDisposition) {
        const match = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
        );
        if (match && match[1]) {
          fileName = match[1].replace(/['"]/g, "");
        }
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },

    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleString();
    },

    showMessage(message, type) {
      this.message = message;
      this.messageType = type;
      this.messageShow = true;
    },
  },

  created() {
    if (this.programmeSelectionPending) {
      return;
    } else if (this.getUser && this.getUser.programme) {
      this.loadData();
    } else {
      this.success();
    }
    this.noHeader && this.$emit("update-title", "Programme Documents");
  },
};
</script>

<style scoped>
</style>
